const ROTATION_VALUES = [0, 90, 180, 270];
const SVG_LENGTH_TO_PX = {
  px: 1,
  in: 96,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  pt: 96 / 72,
  pc: 16,
  q: 96 / 101.6,
};

const parseSvgLengthToPx = (value) => {
  const match = String(value || '').trim().match(/^([+-]?\d*\.?\d+)([a-z%]*)$/i);
  if (!match) {
    return NaN;
  }

  const amount = Number.parseFloat(match[1]);
  const unit = String(match[2] || '').toLowerCase();
  if (!Number.isFinite(amount)) {
    return NaN;
  }

  if (!unit || unit === 'px') {
    return amount;
  }

  if (unit === '%' || !SVG_LENGTH_TO_PX[unit]) {
    return amount;
  }

  return amount * SVG_LENGTH_TO_PX[unit];
};

export const normalizeRotation = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  const normalized = ((Math.round(numeric) % 360) + 360) % 360;
  return ROTATION_VALUES.includes(normalized) ? normalized : 0;
};

export const parseSvgBox = (svgContent) => {
  if (!svgContent || typeof window === 'undefined') {
    return {
      minX: 0,
      minY: 0,
      width: 1000,
      height: 1000,
    };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svg = doc.documentElement;
  const viewBox = svg?.getAttribute('viewBox');

  if (viewBox) {
    const parts = viewBox.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every(Number.isFinite)) {
      return {
        minX: parts[0],
        minY: parts[1],
        width: parts[2],
        height: parts[3],
      };
    }
  }

  const width = parseSvgLengthToPx(svg?.getAttribute('width') || '');
  const height = parseSvgLengthToPx(svg?.getAttribute('height') || '');

  return {
    minX: 0,
    minY: 0,
    width: Number.isFinite(width) && width > 0 ? width : 1000,
    height: Number.isFinite(height) && height > 0 ? height : 1000,
  };
};

export const getRotatedBox = (box, rotation) => {
  const normalizedRotation = normalizeRotation(rotation);
  if (normalizedRotation === 90 || normalizedRotation === 270) {
    return {
      minX: box.minX,
      minY: box.minY,
      width: box.height,
      height: box.width,
    };
  }

  return { ...box };
};

export const rotatePoint = (point, box, rotation) => {
  const normalizedRotation = normalizeRotation(rotation);
  const relativeX = point.x - box.minX;
  const relativeY = point.y - box.minY;

  switch (normalizedRotation) {
    case 90:
      return {
        x: box.minX + (box.height - relativeY),
        y: box.minY + relativeX,
      };
    case 180:
      return {
        x: box.minX + (box.width - relativeX),
        y: box.minY + (box.height - relativeY),
      };
    case 270:
      return {
        x: box.minX + relativeY,
        y: box.minY + (box.width - relativeX),
      };
    default:
      return { ...point };
  }
};

export const unrotatePoint = (point, box, rotation) => {
  const normalizedRotation = normalizeRotation(rotation);
  const relativeX = point.x - box.minX;
  const relativeY = point.y - box.minY;

  switch (normalizedRotation) {
    case 90:
      return {
        x: box.minX + relativeY,
        y: box.minY + (box.height - relativeX),
      };
    case 180:
      return {
        x: box.minX + (box.width - relativeX),
        y: box.minY + (box.height - relativeY),
      };
    case 270:
      return {
        x: box.minX + (box.width - relativeY),
        y: box.minY + relativeX,
      };
    default:
      return { ...point };
  }
};

export const transformPointBetweenRotations = (point, box, fromRotation, toRotation) => {
  const unrotatedPoint = unrotatePoint(point, box, fromRotation);
  return rotatePoint(unrotatedPoint, box, toRotation);
};

const getPinX = (pin) => pin?.position_x ?? pin?.position?.x ?? null;
const getPinY = (pin) => pin?.position_y ?? pin?.position?.y ?? null;

export const rotatePins = (pins, svgContent, rotation) => {
  const normalizedRotation = normalizeRotation(rotation);
  if (normalizedRotation === 0) {
    return Array.isArray(pins) ? pins.map((pin) => ({ ...pin })) : [];
  }

  const box = parseSvgBox(svgContent);

  return (Array.isArray(pins) ? pins : []).map((pin) => {
    const x = Number(getPinX(pin));
    const y = Number(getPinY(pin));
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return { ...pin };
    }

    const rotated = rotatePoint({ x, y }, box, normalizedRotation);
    return {
      ...pin,
      position_x: Number(rotated.x.toFixed(2)),
      position_y: Number(rotated.y.toFixed(2)),
    };
  });
};

export const DEFAULT_CALLOUT_LABEL_SCALE = 0.55;
export const MIN_CALLOUT_LABEL_SCALE = 0.2;
export const MAX_CALLOUT_LABEL_SCALE = 4;
export const DEFAULT_CALLOUT_TEXT_SCALE = DEFAULT_CALLOUT_LABEL_SCALE;
export const MIN_CALLOUT_TEXT_SCALE = MIN_CALLOUT_LABEL_SCALE;
export const MAX_CALLOUT_TEXT_SCALE = MAX_CALLOUT_LABEL_SCALE;
export const DEFAULT_CALLOUT_BUBBLE_SCALE = DEFAULT_CALLOUT_LABEL_SCALE;
export const MIN_CALLOUT_BUBBLE_SCALE = MIN_CALLOUT_LABEL_SCALE;
export const MAX_CALLOUT_BUBBLE_SCALE = MAX_CALLOUT_LABEL_SCALE;
export const DEFAULT_CALLOUT_BUBBLE_WIDTH = DEFAULT_CALLOUT_BUBBLE_SCALE;
export const MIN_CALLOUT_BUBBLE_WIDTH = MIN_CALLOUT_BUBBLE_SCALE;
export const MAX_CALLOUT_BUBBLE_WIDTH = MAX_CALLOUT_BUBBLE_SCALE;
export const DEFAULT_CALLOUT_BUBBLE_HEIGHT = DEFAULT_CALLOUT_BUBBLE_SCALE;
export const MIN_CALLOUT_BUBBLE_HEIGHT = MIN_CALLOUT_BUBBLE_SCALE;
export const MAX_CALLOUT_BUBBLE_HEIGHT = MAX_CALLOUT_BUBBLE_SCALE;
export const PIN_CALLOUT_TYPE = 'pin';
export const CUSTOM_CALLOUT_TYPE = 'custom';
export const DEFAULT_CUSTOM_CALLOUT_GROUP_NAME = 'Custom';
export const DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR = '#475569';

const BASE_CALLOUT_MIN_WIDTH = 52;
const BASE_CALLOUT_MAX_WIDTH = 220;
const BASE_CALLOUT_CHAR_WIDTH = 7.2;
const BASE_CALLOUT_HORIZONTAL_PADDING = 18;
const BASE_CALLOUT_BOX_HEIGHT = 24;
const BASE_CALLOUT_FONT_SIZE = 12;
const BASE_CALLOUT_VERTICAL_PADDING = BASE_CALLOUT_BOX_HEIGHT - BASE_CALLOUT_FONT_SIZE;
const BASE_CALLOUT_CORNER_RADIUS = 6;
const BASE_CALLOUT_ELBOW_OFFSET = 14;
export const DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH = 1.4;
export const MIN_CALLOUT_BUBBLE_STROKE_WIDTH = 0.5;
export const MAX_CALLOUT_BUBBLE_STROKE_WIDTH = 8;
export const MIN_CALLOUT_BUBBLE_RADIUS = 0;
export const MAX_CALLOUT_BUBBLE_RADIUS = 48;
export const MIN_CALLOUT_LINE_STROKE_WIDTH = 0.5;
export const MAX_CALLOUT_LINE_STROKE_WIDTH = 8;
export const MIN_CALLOUT_ANCHOR_RADIUS = 1;
export const MAX_CALLOUT_ANCHOR_RADIUS = 24;
const CALLOUT_REFERENCE_LONG_SIDE = 1000;
const CALLOUT_AUTO_SIZE_MIN = 1;
const CALLOUT_AUTO_SIZE_MAX = 2.4;
const CALLOUT_GROUP_COLOR_FALLBACKS = {
  analog: '#45b7d1',
  communication: '#96ceb4',
  custom: DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR,
  digital: '#4ecdc4',
  ground: '#000000',
  power: '#ff6b6b',
  special: '#ff9ff3',
};

const clampNumber = (value, min, max) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return min;
  }

  return Math.max(min, Math.min(max, numeric));
};

export const getCalloutAutoSizeMultiplier = (box, options = {}) => {
  if (options.normalizeCalloutSize === false) {
    return 1;
  }

  const referenceLongSide = Number.isFinite(Number(options.calloutReferenceLongSide))
    ? Math.max(1, Number(options.calloutReferenceLongSide))
    : CALLOUT_REFERENCE_LONG_SIDE;

  const minMultiplier = Number.isFinite(Number(options.calloutAutoSizeMin))
    ? Number(options.calloutAutoSizeMin)
    : CALLOUT_AUTO_SIZE_MIN;

  const maxMultiplier = Number.isFinite(Number(options.calloutAutoSizeMax))
    ? Number(options.calloutAutoSizeMax)
    : CALLOUT_AUTO_SIZE_MAX;

  const longSide = Math.max(Number(box?.width) || 0, Number(box?.height) || 0);

  if (!longSide) {
    return 1;
  }

  return Number(
    clampNumber(longSide / referenceLongSide, minMultiplier, maxMultiplier).toFixed(4)
  );
};

const multiplyMetric = (value, multiplier, min = 0) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return value;
  }

  return Number(Math.max(min, numeric * multiplier).toFixed(2));
};

const normalizeGroupKey = (groupName = '') => String(groupName || '').trim().toLowerCase();

export const normalizeCalloutGroupColor = (value) => {
  const color = String(value || '').trim();

  if (!color) {
    return '';
  }

  if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) {
    return color;
  }

  if (/^rgba?\(\s*(\d{1,3}(?:\.\d+)?)\s*,\s*(\d{1,3}(?:\.\d+)?)\s*,\s*(\d{1,3}(?:\.\d+)?)(?:\s*,\s*[\d.]+\s*)?\)$/i.test(color)) {
    return color;
  }

  return '';
};

export const resolveCalloutGroupColor = (
  groupName = DEFAULT_CUSTOM_CALLOUT_GROUP_NAME,
  groupColor = ''
) => {
  const explicitColor = normalizeCalloutGroupColor(groupColor);
  if (explicitColor) {
    return explicitColor;
  }

  return CALLOUT_GROUP_COLOR_FALLBACKS[normalizeGroupKey(groupName)] || DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR;
};

const inferCalloutStrokeColor = (labelText = '') => {
  const normalizedLabel = String(labelText || '').trim().toUpperCase();

  if (!normalizedLabel) {
    return DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR;
  }

  if (/\bLED(?:_BUILTIN)?\b/.test(normalizedLabel)) {
    return '#be185d';
  }

  if (/(?:^|[^A-Z0-9])(?:TX|RX|TX0|TX1|RX0|RX1|TXI|RXI|TXO|RXO|SDA|SCL|SPI|I2C|IIC|UART|MOSI|MISO|SCK|SCLK|CS|SS|MCLK|BCLK)(?:[^A-Z0-9]|$)/.test(normalizedLabel)) {
    return '#7e22ce';
  }

  if (/^A\d+$/.test(normalizedLabel) || /\bADC\d*\b/.test(normalizedLabel)) {
    return '#15803d';
  }

  if (/(?:^|[^A-Z0-9])(?:GPIO\d*|IO\d+|D\d+|GPIO|IO)(?:[^A-Z0-9]|$)/.test(normalizedLabel)) {
    return '#2563eb';
  }

  return DEFAULT_CUSTOM_CALLOUT_GROUP_COLOR;
};

const isGroundCalloutLabel = (labelText = '') => {
  const normalizedLabel = String(labelText || '').trim().toUpperCase();

  return [
    'GND',
    'GROUND',
    'AGND',
    'PGND',
    'GNDA',
    'GNDD',
  ].includes(normalizedLabel) || /^(?:A?P?GND|GND\d+|GROUND\d*)$/.test(normalizedLabel);
};

const resolveCalloutBubbleStrokeColor = (callout = {}, pin = null) => {
  const groupName = String(
    pin
      ? pin?.group_name ?? pin?.groupName ?? 'Other'
      : callout?.group_name ?? callout?.groupName ?? DEFAULT_CUSTOM_CALLOUT_GROUP_NAME
  ).trim() || DEFAULT_CUSTOM_CALLOUT_GROUP_NAME;
  const labelText = pin
    ? String(
        callout?.label
        ?? pin?.pin_name
        ?? pin?.pinName
        ?? pin?.pin_number
        ?? pin?.pinNumber
        ?? ''
      ).trim()
    : String(callout?.label ?? '').trim();
  const pinRole = String(
    pin
      ? pin?.pin_role ?? pin?.pinRole ?? callout?.pin_role ?? callout?.pinRole ?? ''
      : callout?.pin_role ?? callout?.pinRole ?? ''
  ).trim().toLowerCase();

  if (pinRole === 'ground' || isGroundCalloutLabel(labelText)) {
    return CALLOUT_GROUP_COLOR_FALLBACKS.ground;
  }

  const storedGroupColor = pin
    ? pin?.group_color ?? pin?.groupColor ?? callout?.group_color ?? callout?.groupColor
    : callout?.group_color ?? callout?.groupColor;
  const explicitGroupColor = normalizeCalloutGroupColor(storedGroupColor);

  if (explicitGroupColor) {
    return explicitGroupColor;
  }

  return inferCalloutStrokeColor(labelText || groupName);
};

const parseColorChannels = (color) => {
  const trimmed = String(color || '').trim();
  if (!trimmed || trimmed === 'none' || trimmed === 'transparent') {
    return null;
  }

  const rgbMatch = trimmed.match(/^rgba?\(\s*(\d{1,3}(?:\.\d+)?)\s*,\s*(\d{1,3}(?:\.\d+)?)\s*,\s*(\d{1,3}(?:\.\d+)?)(?:\s*,\s*[\d.]+\s*)?\)$/i);
  if (rgbMatch) {
    return {
      red: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[1])))),
      green: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[2])))),
      blue: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[3])))),
    };
  }

  let normalized = trimmed.replace(/^#/, '');
  if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) {
    return null;
  }

  if (normalized.length === 3) {
    normalized = normalized.split('').map((token) => `${token}${token}`).join('');
  }

  return {
    red: Number.parseInt(normalized.slice(0, 2), 16),
    green: Number.parseInt(normalized.slice(2, 4), 16),
    blue: Number.parseInt(normalized.slice(4, 6), 16),
  };
};

const getCalloutSelectedFillColor = (strokeColor) => {
  const channels = parseColorChannels(strokeColor);
  if (!channels) {
    return 'rgb(201, 219, 254)';
  }

  const mixChannel = (channel) => Math.round(channel + ((255 - channel) * 0.72));
  return `rgb(${mixChannel(channels.red)}, ${mixChannel(channels.green)}, ${mixChannel(channels.blue)})`;
};

export const normalizeCalloutLabelScale = (value, fallback = DEFAULT_CALLOUT_LABEL_SCALE) => {
  const normalizedFallback = Number.isFinite(Number(fallback)) ? Number(fallback) : DEFAULT_CALLOUT_LABEL_SCALE;
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return Number(Math.min(MAX_CALLOUT_LABEL_SCALE, Math.max(MIN_CALLOUT_LABEL_SCALE, normalizedFallback)).toFixed(2));
  }

  return Number(Math.min(MAX_CALLOUT_LABEL_SCALE, Math.max(MIN_CALLOUT_LABEL_SCALE, numeric)).toFixed(2));
};

export const normalizeCalloutTextScale = (value, fallback = DEFAULT_CALLOUT_TEXT_SCALE) => {
  const normalizedFallback = Number.isFinite(Number(fallback)) ? Number(fallback) : DEFAULT_CALLOUT_TEXT_SCALE;
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return Number(Math.min(MAX_CALLOUT_TEXT_SCALE, Math.max(MIN_CALLOUT_TEXT_SCALE, normalizedFallback)).toFixed(2));
  }

  return Number(Math.min(MAX_CALLOUT_TEXT_SCALE, Math.max(MIN_CALLOUT_TEXT_SCALE, numeric)).toFixed(2));
};

export const normalizeCalloutBubbleScale = (value, fallback = DEFAULT_CALLOUT_BUBBLE_SCALE) => {
  const normalizedFallback = Number.isFinite(Number(fallback)) ? Number(fallback) : DEFAULT_CALLOUT_BUBBLE_SCALE;
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return Number(Math.min(MAX_CALLOUT_BUBBLE_SCALE, Math.max(MIN_CALLOUT_BUBBLE_SCALE, normalizedFallback)).toFixed(2));
  }

  return Number(Math.min(MAX_CALLOUT_BUBBLE_SCALE, Math.max(MIN_CALLOUT_BUBBLE_SCALE, numeric)).toFixed(2));
};

export const normalizeCalloutBubbleWidth = (value, fallback = DEFAULT_CALLOUT_BUBBLE_WIDTH) => (
  normalizeCalloutBubbleScale(value, fallback)
);

export const normalizeCalloutBubbleHeight = (value, fallback = DEFAULT_CALLOUT_BUBBLE_HEIGHT) => (
  normalizeCalloutBubbleScale(value, fallback)
);

const normalizeCalloutVisualValue = (value, min, max, fallback) => {
  const normalizedFallback = Number.isFinite(Number(fallback)) ? Number(fallback) : min;
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return Number(Math.min(max, Math.max(min, normalizedFallback)).toFixed(2));
  }

  return Number(Math.min(max, Math.max(min, numeric)).toFixed(2));
};

const getLegacyCalloutCornerRadius = (scale = DEFAULT_CALLOUT_BUBBLE_SCALE) => {
  return Number(Math.max(4, BASE_CALLOUT_CORNER_RADIUS * normalizeCalloutBubbleScale(scale)).toFixed(2));
};

const getLegacyCalloutLineWidth = (scale = DEFAULT_CALLOUT_LABEL_SCALE) => {
  return Number(Math.max(1.1, BASE_CALLOUT_LINE_WIDTH * normalizeCalloutLabelScale(scale)).toFixed(2));
};

const getLegacyCalloutAnchorVisibleRadius = (scale = DEFAULT_CALLOUT_LABEL_SCALE) => {
  return Number(Math.max(2, BASE_CALLOUT_ANCHOR_VISIBLE_RADIUS * normalizeCalloutLabelScale(scale)).toFixed(2));
};

export const normalizeCalloutBubbleRadius = (value, fallback = getLegacyCalloutCornerRadius(DEFAULT_CALLOUT_BUBBLE_SCALE)) => {
  return normalizeCalloutVisualValue(value, MIN_CALLOUT_BUBBLE_RADIUS, MAX_CALLOUT_BUBBLE_RADIUS, fallback);
};

export const normalizeCalloutBubbleStrokeWidth = (value, fallback = DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH) => {
  return normalizeCalloutVisualValue(value, MIN_CALLOUT_BUBBLE_STROKE_WIDTH, MAX_CALLOUT_BUBBLE_STROKE_WIDTH, fallback);
};

export const normalizeCalloutLineStrokeWidth = (value, fallback = getLegacyCalloutLineWidth(DEFAULT_CALLOUT_LABEL_SCALE)) => {
  return normalizeCalloutVisualValue(value, MIN_CALLOUT_LINE_STROKE_WIDTH, MAX_CALLOUT_LINE_STROKE_WIDTH, fallback);
};

export const normalizeCalloutAnchorRadius = (value, fallback = getLegacyCalloutAnchorVisibleRadius(DEFAULT_CALLOUT_LABEL_SCALE)) => {
  return normalizeCalloutVisualValue(value, MIN_CALLOUT_ANCHOR_RADIUS, MAX_CALLOUT_ANCHOR_RADIUS, fallback);
};

export const getCalloutAppearance = (callout = {}) => {
  const legacyScale = normalizeCalloutLabelScale(
    callout?.label_scale ?? callout?.labelScale ?? callout?.bubble_scale ?? callout?.bubbleScale ?? callout?.scale,
    DEFAULT_CALLOUT_LABEL_SCALE
  );
  const textScale = normalizeCalloutTextScale(
    callout?.text_scale ?? callout?.textScale,
    legacyScale
  );
  const bubbleScale = normalizeCalloutBubbleScale(
    callout?.bubble_scale ?? callout?.bubbleScale ?? callout?.label_scale ?? callout?.labelScale ?? callout?.scale,
    legacyScale
  );
  const bubbleWidth = normalizeCalloutBubbleWidth(
    callout?.bubble_width ?? callout?.bubbleWidth ?? callout?.bubble_width_scale ?? callout?.bubbleWidthScale,
    bubbleScale
  );
  const bubbleHeight = normalizeCalloutBubbleHeight(
    callout?.bubble_height ?? callout?.bubbleHeight ?? callout?.bubble_height_scale ?? callout?.bubbleHeightScale,
    bubbleScale
  );
  const bubbleRadius = normalizeCalloutBubbleRadius(
    callout?.bubble_radius ?? callout?.bubbleRadius,
    getLegacyCalloutCornerRadius(Math.min(bubbleWidth, bubbleHeight))
  );
  const bubbleStrokeWidth = normalizeCalloutBubbleStrokeWidth(
    callout?.bubble_stroke_width ?? callout?.bubbleStrokeWidth ?? callout?.outline_width ?? callout?.outlineWidth,
    DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH
  );
  const lineStrokeWidth = normalizeCalloutLineStrokeWidth(
    callout?.line_stroke_width ?? callout?.lineStrokeWidth,
    getLegacyCalloutLineWidth(legacyScale)
  );
  const anchorRadius = normalizeCalloutAnchorRadius(
    callout?.anchor_radius ?? callout?.anchorRadius ?? callout?.anchor_bubble_size ?? callout?.anchorBubbleSize,
    getLegacyCalloutAnchorVisibleRadius(legacyScale)
  );

  return {
    legacyScale,
    textScale,
    bubbleScale,
    bubbleWidth,
    bubbleHeight,
    bubbleRadius,
    bubbleStrokeWidth,
    lineStrokeWidth,
    anchorRadius,
  };
};

export const estimateLabelWidth = (
  label = '',
  textScale = DEFAULT_CALLOUT_TEXT_SCALE,
  bubbleWidth = DEFAULT_CALLOUT_BUBBLE_WIDTH
) => {
  const normalizedTextScale = normalizeCalloutTextScale(textScale);
  const normalizedBubbleWidth = normalizeCalloutBubbleWidth(bubbleWidth);
  const maxScale = Math.max(normalizedTextScale, normalizedBubbleWidth);
  const contentWidth = (String(label).length * BASE_CALLOUT_CHAR_WIDTH * normalizedTextScale)
    + (BASE_CALLOUT_HORIZONTAL_PADDING * normalizedBubbleWidth);
  const minWidth = BASE_CALLOUT_MIN_WIDTH * normalizedBubbleWidth;
  const maxWidth = BASE_CALLOUT_MAX_WIDTH * maxScale;

  return Number(Math.max(minWidth, Math.min(maxWidth, contentWidth)).toFixed(2));
};

export const getCalloutBoxHeight = (
  textScale = DEFAULT_CALLOUT_TEXT_SCALE,
  bubbleHeight = DEFAULT_CALLOUT_BUBBLE_HEIGHT
) => {
  const normalizedTextScale = normalizeCalloutTextScale(textScale);
  const normalizedBubbleHeight = normalizeCalloutBubbleHeight(bubbleHeight);
  const minHeight = BASE_CALLOUT_BOX_HEIGHT * normalizedBubbleHeight;
  const paddedTextHeight = (BASE_CALLOUT_FONT_SIZE * normalizedTextScale)
    + (BASE_CALLOUT_VERTICAL_PADDING * normalizedBubbleHeight);

  return Number(Math.max(minHeight, paddedTextHeight).toFixed(2));
};

export const getCalloutFontSize = (textScale = DEFAULT_CALLOUT_TEXT_SCALE) => {
  return Number((BASE_CALLOUT_FONT_SIZE * normalizeCalloutTextScale(textScale)).toFixed(2));
};

export const getCalloutElbowOffset = (
  bubbleWidth = DEFAULT_CALLOUT_BUBBLE_WIDTH,
  bubbleHeight = bubbleWidth
) => {
  const dimensionScale = Math.max(
    normalizeCalloutBubbleWidth(bubbleWidth),
    normalizeCalloutBubbleHeight(bubbleHeight, bubbleWidth)
  );
  return Number(Math.max(10, BASE_CALLOUT_ELBOW_OFFSET * dimensionScale).toFixed(2));
};

export const getCalloutMetrics = (label = '', callout = {}) => {
  const appearance = getCalloutAppearance(callout);
  const width = estimateLabelWidth(label, appearance.textScale, appearance.bubbleWidth);
  const height = getCalloutBoxHeight(appearance.textScale, appearance.bubbleHeight);

  return {
    ...appearance,
    width,
    height,
    fontSize: getCalloutFontSize(appearance.textScale),
    elbowOffset: getCalloutElbowOffset(appearance.bubbleWidth, appearance.bubbleHeight),
    anchorHitRadius: getCalloutAnchorHitRadius(appearance.anchorRadius),
  };
};

export const getPinLayoutKey = (pin) => {
  const id = String(pin?.id ?? '').trim();
  if (id) {
    return `id:${id}`;
  }

  const svgId = String(pin?.svg_id ?? pin?.svgId ?? '').trim();
  if (svgId) {
    return `svg:${svgId}`;
  }

  const pinNumber = String(pin?.pin_number ?? pin?.pinNumber ?? '').trim();
  if (pinNumber) {
    return `num:${pinNumber}`;
  }

  const layoutId = String(pin?.layout_id ?? pin?.layoutId ?? pin?.connector_id ?? pin?.connectorId ?? '').trim();
  if (layoutId) {
    return `layout:${layoutId}`;
  }

  return `name:${String(pin?.pin_name ?? pin?.pinName ?? '').trim()}`;
};

export const getPinLayoutAliases = (pin) => {
  const aliases = [];

  const id = String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? '').trim();
  if (id) aliases.push(`id:${id}`);

  const svgId = String(pin?.svg_id ?? pin?.svgId ?? '').trim();
  if (svgId) aliases.push(`svg:${svgId}`);

  const pinNumber = String(pin?.pin_number ?? pin?.pinNumber ?? '').trim();
  if (pinNumber) aliases.push(`num:${pinNumber}`);

  const layoutId = String(pin?.layout_id ?? pin?.layoutId ?? pin?.connector_id ?? pin?.connectorId ?? '').trim();
  if (layoutId) aliases.push(`layout:${layoutId}`);

  const pinName = String(pin?.pin_name ?? pin?.pinName ?? '').trim();
  if (pinName) aliases.push(`name:${pinName}`);

  return aliases;
};

export const doesPinMatchLayoutKey = (pin, key) => {
  return getPinLayoutAliases(pin).includes(key);
};

const buildFallbackCustomId = (callout, labelX, labelY) => {
  const label = String(callout?.label ?? callout?.text ?? 'custom').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const x = Number.isFinite(labelX) ? Math.round(labelX * 10) : 0;
  const y = Number.isFinite(labelY) ? Math.round(labelY * 10) : 0;
  return `${label || 'custom'}-${Math.abs(x)}-${Math.abs(y)}`;
};

export const getCalloutType = (callout) => {
  const explicitType = String(callout?.callout_type ?? callout?.calloutType ?? callout?.type ?? '').trim().toLowerCase();
  if (explicitType === CUSTOM_CALLOUT_TYPE) {
    return CUSTOM_CALLOUT_TYPE;
  }

  const customId = String(callout?.custom_id ?? callout?.customId ?? '').trim();
  if (customId) {
    return CUSTOM_CALLOUT_TYPE;
  }

  return PIN_CALLOUT_TYPE;
};

export const getCalloutLayoutKey = (callout) => {
  const calloutType = getCalloutType(callout);
  const customId = String(callout?.custom_id ?? callout?.customId ?? callout?.id ?? '').trim();
  if (calloutType === CUSTOM_CALLOUT_TYPE && customId) {
    return `custom:${customId}`;
  }

  const pinId = String(callout?.pin_id ?? callout?.pinId ?? callout?.id ?? '').trim();
  if (pinId && getCalloutType(callout) !== CUSTOM_CALLOUT_TYPE) {
    return `id:${pinId}`;
  }

  const svgId = String(callout?.svg_id ?? callout?.svgId ?? '').trim();
  if (svgId) {
    return `svg:${svgId}`;
  }

  const pinNumber = String(callout?.pin_number ?? callout?.pinNumber ?? '').trim();
  if (pinNumber) {
    return `num:${pinNumber}`;
  }

  return `name:${String(callout?.pin_name ?? callout?.pinName ?? '').trim()}`;
};

export const normalizeCalloutRoutePoints = (routePoints) => {
  return (Array.isArray(routePoints) ? routePoints : [])
    .map((point) => {
      const x = Number(point?.x ?? point?.[0] ?? point?.position_x ?? point?.positionX);
      const y = Number(point?.y ?? point?.[1] ?? point?.position_y ?? point?.positionY);

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }

      return {
        x: Number(x.toFixed(2)),
        y: Number(y.toFixed(2)),
      };
    })
    .filter(Boolean);
};

export const getCalloutRoutePoints = (callout) => (
  normalizeCalloutRoutePoints(callout?.route_points ?? callout?.routePoints)
);

const getOwnCalloutValue = (callout, keys = []) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(callout || {}, key)) {
      return callout[key];
    }
  }

  return undefined;
};

const normalizeOptionalCalloutText = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
};

const normalizeOptionalCalloutNumber = (value, decimals = 3) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Number(numericValue.toFixed(decimals));
};

const normalizeOptionalCalloutBoolean = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (!normalizedValue) {
      return null;
    }

    if (['1', 'true', 'yes', 'on'].includes(normalizedValue)) {
      return true;
    }

    if (['0', 'false', 'no', 'off'].includes(normalizedValue)) {
      return false;
    }
  }

  return Boolean(value);
};

const normalizeOptionalCalloutTags = (value) => {
  const tagValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];

  return Array.from(new Set(
    tagValues
      .map((tagValue) => String(tagValue ?? '').trim())
      .filter(Boolean)
  ));
};

export const normalizePinCallouts = (callouts) => {
  return (Array.isArray(callouts) ? callouts : [])
    .map((callout) => {
      const labelX = Number(callout?.label_x ?? callout?.labelX);
      const labelY = Number(callout?.label_y ?? callout?.labelY);
      const anchorX = Number(callout?.anchor_x ?? callout?.anchorX);
      const anchorY = Number(callout?.anchor_y ?? callout?.anchorY);
      const calloutType = getCalloutType(callout);
      const customId = String(callout?.custom_id ?? callout?.customId ?? '').trim()
        || (calloutType === CUSTOM_CALLOUT_TYPE ? buildFallbackCustomId(callout, labelX, labelY) : '');
      const appearance = getCalloutAppearance(callout);
      const rawRotationDeg = Number(callout?.rotation_deg ?? callout?.rotationDeg ?? callout?.label_rotation ?? 0);
      const rotationDeg = Number.isFinite(rawRotationDeg)
        ? ((Math.round(rawRotationDeg / 90) * 90) % 360 + 360) % 360
        : 0;
      const routePoints = getCalloutRoutePoints(callout);

      const allowedLineStyles = ['orthogonal', 'diagonal45'];
      const lineStyle = String(
        callout?.line_style ?? callout?.lineStyle ?? 'orthogonal'
      ).trim().toLowerCase();

      const allowedAttachSides = ['auto', 'top', 'right', 'bottom', 'left'];
      const lineAttachSide = String(
        callout?.line_attach_side ?? callout?.lineAttachSide ?? 'auto'
      ).trim().toLowerCase();

      if (!Number.isFinite(labelX) || !Number.isFinite(labelY)) {
        return null;
      }

      const normalizedCallout = {
        callout_type: calloutType,
        pin_id: String(callout?.pin_id ?? callout?.pinId ?? '').trim(),
        pin_number: String(callout?.pin_number ?? callout?.pinNumber ?? '').trim(),
        svg_id: String(callout?.svg_id ?? callout?.svgId ?? '').trim(),
        pin_name: String(callout?.pin_name ?? callout?.pinName ?? '').trim(),
        label: String(callout?.label ?? callout?.text ?? '').trim(),
        label_x: Number(labelX.toFixed(2)),
        label_y: Number(labelY.toFixed(2)),
        label_scale: appearance.bubbleScale,
        text_scale: appearance.textScale,
        bubble_scale: appearance.bubbleScale,
        bubble_width: appearance.bubbleWidth,
        bubble_height: appearance.bubbleHeight,
        bubble_radius: appearance.bubbleRadius,
        bubble_stroke_width: appearance.bubbleStrokeWidth,
        line_stroke_width: appearance.lineStrokeWidth,
        anchor_radius: appearance.anchorRadius,
        rotation_deg: Number.isFinite(rotationDeg) ? Number(rotationDeg.toFixed(2)) : 0,
        line_attach_side: allowedAttachSides.includes(lineAttachSide) ? lineAttachSide : 'auto',
        line_style: allowedLineStyles.includes(lineStyle) ? lineStyle : 'orthogonal',
      };

      if (calloutType === CUSTOM_CALLOUT_TYPE) {
        normalizedCallout.custom_id = customId;
        const groupName = String(
          callout?.group_name ?? callout?.groupName ?? DEFAULT_CUSTOM_CALLOUT_GROUP_NAME
        ).trim() || DEFAULT_CUSTOM_CALLOUT_GROUP_NAME;
        normalizedCallout.group_name = groupName;
        normalizedCallout.group_color = resolveCalloutGroupColor(
          groupName,
          callout?.group_color ?? callout?.groupColor
        );
        if (!normalizedCallout.label) {
          normalizedCallout.label = 'Custom Label';
        }

        const primaryFunction = normalizeOptionalCalloutText(callout?.functions);
        if (primaryFunction) {
          normalizedCallout.functions = primaryFunction;
        }

        const alternateFunctions = normalizeOptionalCalloutText(
          callout?.alternate_functions ?? callout?.alternateFunctions
        );
        if (alternateFunctions) {
          normalizedCallout.alternate_functions = alternateFunctions;
        }

        const description = normalizeOptionalCalloutText(callout?.description);
        if (description) {
          normalizedCallout.description = description;
        }

        const pinRole = normalizeOptionalCalloutText(callout?.pin_role ?? callout?.pinRole);
        if (pinRole) {
          normalizedCallout.pin_role = pinRole;
        }

        const currentLimitMa = normalizeOptionalCalloutNumber(
          getOwnCalloutValue(callout, ['current_limit_ma', 'currentLimitMa'])
        );
        if (currentLimitMa !== null) {
          normalizedCallout.current_limit_ma = currentLimitMa;
        }

        const voltageMinV = normalizeOptionalCalloutNumber(
          getOwnCalloutValue(callout, ['voltage_min_v', 'voltageMinV'])
        );
        if (voltageMinV !== null) {
          normalizedCallout.voltage_min_v = voltageMinV;
        }

        const voltageMaxV = normalizeOptionalCalloutNumber(
          getOwnCalloutValue(callout, ['voltage_max_v', 'voltageMaxV'])
        );
        if (voltageMaxV !== null) {
          normalizedCallout.voltage_max_v = voltageMaxV;
        }

        const capabilityTags = normalizeOptionalCalloutTags(
          getOwnCalloutValue(callout, ['capability_tags', 'capabilityTags'])
        );
        if (capabilityTags.length > 0) {
          normalizedCallout.capability_tags = capabilityTags;
        }

        const is5vTolerant = normalizeOptionalCalloutBoolean(
          getOwnCalloutValue(callout, ['is_5v_tolerant', 'is5vTolerant'])
        );
        if (is5vTolerant !== null) {
          normalizedCallout.is_5v_tolerant = is5vTolerant;
        }

        const useWithCaution = normalizeOptionalCalloutBoolean(
          getOwnCalloutValue(callout, ['use_with_caution', 'useWithCaution'])
        );
        if (useWithCaution !== null) {
          normalizedCallout.use_with_caution = useWithCaution;
        }

        const cautionReason = normalizeOptionalCalloutText(
          callout?.caution_reason ?? callout?.cautionReason
        );
        if (cautionReason) {
          normalizedCallout.caution_reason = cautionReason;
        }
      }

      if (Number.isFinite(anchorX) && Number.isFinite(anchorY)) {
        normalizedCallout.anchor_x = Number(anchorX.toFixed(2));
        normalizedCallout.anchor_y = Number(anchorY.toFixed(2));
      }

      if (routePoints.length > 0) {
        normalizedCallout.route_points = routePoints;
      }

      return normalizedCallout;
    })
    .filter(Boolean);
};

export const createInitialPinCallouts = (pins, svgContent, rotation) => {
  const rotatedPins = rotatePins(pins, svgContent, rotation);
  const rotatedBox = getRotatedBox(parseSvgBox(svgContent), rotation);
  const margin = Math.max(36, Math.min(rotatedBox.width, rotatedBox.height) * 0.08);

  return rotatedPins
    .filter((pin) => Number.isFinite(Number(getPinX(pin))) && Number.isFinite(Number(getPinY(pin))))
    .map((pin) => {
      const pinName = String(pin.pin_name ?? pin.pinName ?? pin.pin_number ?? pin.pinNumber ?? 'Pin');
      const pinX = Number(getPinX(pin));
      const pinY = Number(getPinY(pin));
      const labelScale = DEFAULT_CALLOUT_LABEL_SCALE;
      const labelWidth = estimateLabelWidth(pinName, labelScale, labelScale);
      const boxHeight = getCalloutBoxHeight(labelScale, labelScale);
      const distances = {
        left: Math.abs(pinX - rotatedBox.minX),
        right: Math.abs((rotatedBox.minX + rotatedBox.width) - pinX),
        top: Math.abs(pinY - rotatedBox.minY),
        bottom: Math.abs((rotatedBox.minY + rotatedBox.height) - pinY),
      };

      const nearestSide = Object.entries(distances).sort((a, b) => a[1] - b[1])[0]?.[0] || 'right';
      let labelX = rotatedBox.minX + rotatedBox.width + margin;
      let labelY = pinY - (boxHeight / 2);

      if (nearestSide === 'left') {
        labelX = rotatedBox.minX - labelWidth - margin;
        labelY = pinY - (boxHeight / 2);
      } else if (nearestSide === 'right') {
        labelX = rotatedBox.minX + rotatedBox.width + margin;
        labelY = pinY - (boxHeight / 2);
      } else if (nearestSide === 'top') {
        labelX = pinX - (labelWidth / 2);
        labelY = rotatedBox.minY - margin - boxHeight;
      } else if (nearestSide === 'bottom') {
        labelX = pinX - (labelWidth / 2);
        labelY = rotatedBox.minY + rotatedBox.height + margin;
      }

      return {
        pin_number: String(pin.pin_number ?? pin.pinNumber ?? '').trim(),
        svg_id: String(pin.svg_id ?? pin.svgId ?? '').trim(),
        pin_name: String(pin.pin_name ?? pin.pinName ?? '').trim(),
        label: pinName,
        label_x: Number(labelX.toFixed(2)),
        label_y: Number(labelY.toFixed(2)),
        label_scale: labelScale,
        text_scale: labelScale,
        bubble_scale: labelScale,
        bubble_width: labelScale,
        bubble_height: labelScale,
        bubble_radius: getLegacyCalloutCornerRadius(labelScale),
        bubble_stroke_width: DEFAULT_CALLOUT_BUBBLE_STROKE_WIDTH,
        line_stroke_width: getLegacyCalloutLineWidth(labelScale),
        anchor_radius: getLegacyCalloutAnchorVisibleRadius(labelScale),
      };
    });
};

export const rotateCallouts = (callouts, svgContent, fromRotation, toRotation) => {
  const originalBox = parseSvgBox(svgContent);

  return normalizePinCallouts(callouts).map((callout) => {
    const transformedLabel = transformPointBetweenRotations(
      {
        x: callout.label_x,
        y: callout.label_y,
      },
      originalBox,
      fromRotation,
      toRotation
    );

    const nextCallout = {
      ...callout,
      label_x: Number(transformedLabel.x.toFixed(2)),
      label_y: Number(transformedLabel.y.toFixed(2)),
    };

    if (Number.isFinite(Number(callout.anchor_x)) && Number.isFinite(Number(callout.anchor_y))) {
      const transformedAnchor = transformPointBetweenRotations(
        {
          x: Number(callout.anchor_x),
          y: Number(callout.anchor_y),
        },
        originalBox,
        fromRotation,
        toRotation
      );

      nextCallout.anchor_x = Number(transformedAnchor.x.toFixed(2));
      nextCallout.anchor_y = Number(transformedAnchor.y.toFixed(2));
    }

    const routePoints = getCalloutRoutePoints(callout);
    if (routePoints.length > 0) {
      nextCallout.route_points = routePoints.map((routePoint) => {
        const transformedRoutePoint = transformPointBetweenRotations(
          routePoint,
          originalBox,
          fromRotation,
          toRotation
        );

        return {
          x: Number(transformedRoutePoint.x.toFixed(2)),
          y: Number(transformedRoutePoint.y.toFixed(2)),
        };
      });
    }

    return nextCallout;
  });
};

export const validatePinCallouts = (pins, callouts) => {
  const normalizedCallouts = normalizePinCallouts(callouts);

  const pinMap = new Map();
  (Array.isArray(pins) ? pins : []).forEach((pin) => {
    getPinLayoutAliases(pin).forEach((key) => {
      if (!pinMap.has(key)) {
        pinMap.set(key, pin);
      }
    });
  });
  const errors = [];
  const warnings = [];
  const duplicateTracker = new Set();

  normalizedCallouts.forEach((callout, index) => {
    const key = getCalloutLayoutKey(callout);
    if (duplicateTracker.has(key)) {
      errors.push(`Callout ${callout.label || index + 1} is duplicated.`);
      return;
    }

    duplicateTracker.add(key);

    if (getCalloutType(callout) === CUSTOM_CALLOUT_TYPE) {
      if (!callout.custom_id) {
        errors.push(`Custom callout ${index + 1} is missing an identifier.`);
      }

      if (!Number.isFinite(Number(callout.anchor_x)) || !Number.isFinite(Number(callout.anchor_y))) {
        errors.push(`Custom callout ${callout.label || index + 1} must include an anchor point.`);
      }

      return;
    }

    if (!pinMap.has(key)) {
      errors.push(`Callout ${index + 1} is not linked to a detected pin.`);
      return;
    }
  });

  const linkedCalloutKeys = new Set(
    normalizedCallouts
      .filter((callout) => getCalloutType(callout) !== CUSTOM_CALLOUT_TYPE)
      .map((callout) => getCalloutLayoutKey(callout))
  );
  const unplacedPins = (Array.isArray(pins) ? pins : []).filter((pin) =>
    !getPinLayoutAliases(pin).some((key) => linkedCalloutKeys.has(key))
  );
  if (unplacedPins.length > 0) {
    warnings.push(`${unplacedPins.length} detected pins do not have labels yet.`);
  }

  if (normalizedCallouts.length === 0) {
    warnings.push('No pin labels have been placed yet.');
  }

  return {
    errors,
    warnings,
    unplacedPins,
    callouts: normalizedCallouts,
  };
};

const createSvgElement = (doc, name, attrs = {}) => {
  const element = doc.createElementNS('http://www.w3.org/2000/svg', name);
  Object.entries(attrs).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, String(value));
    }
  });
  return element;
};

export const getRotatedBubbleSideMidpoint = ({
  centerX,
  centerY,
  width,
  height,
  rotationDeg,
  side,
}) => {
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  const localPoints = {
    top: { x: 0, y: -halfHeight },
    right: { x: halfWidth, y: 0 },
    bottom: { x: 0, y: halfHeight },
    left: { x: -halfWidth, y: 0 },
  };

  const point = localPoints[side] || localPoints.right;
  const angle = (rotationDeg * Math.PI) / 180;

  return {
    x: centerX + (point.x * Math.cos(angle)) - (point.y * Math.sin(angle)),
    y: centerY + (point.x * Math.sin(angle)) + (point.y * Math.cos(angle)),
  };
};

const BASE_CALLOUT_ANCHOR_VISIBLE_RADIUS = 2.4;
const BASE_CALLOUT_LINE_WIDTH = 1.4;

const getCalloutLineWidth = (value, fallback = getLegacyCalloutLineWidth(DEFAULT_CALLOUT_LABEL_SCALE)) => {
  return normalizeCalloutLineStrokeWidth(value, fallback);
};

const getCalloutAnchorVisibleRadius = (value, fallback = getLegacyCalloutAnchorVisibleRadius(DEFAULT_CALLOUT_LABEL_SCALE)) => {
  return normalizeCalloutAnchorRadius(value, fallback);
};

const getCalloutAnchorHitRadius = (visibleRadius = getLegacyCalloutAnchorVisibleRadius(DEFAULT_CALLOUT_LABEL_SCALE)) => {
  const normalizedRadius = normalizeCalloutAnchorRadius(visibleRadius, getLegacyCalloutAnchorVisibleRadius(DEFAULT_CALLOUT_LABEL_SCALE));
  return Number(Math.max(8, normalizedRadius * 3.2).toFixed(2));
};

const getRotationTransform = (box, rotation) => {
  const normalizedRotation = normalizeRotation(rotation);

  switch (normalizedRotation) {
    case 90:
      return {
        transform: `translate(${box.minX + box.height + box.minY} ${box.minY - box.minX}) rotate(90)`,
        box: getRotatedBox(box, normalizedRotation),
      };
    case 180:
      return {
        transform: `translate(${(2 * box.minX) + box.width} ${(2 * box.minY) + box.height}) rotate(180)`,
        box: getRotatedBox(box, normalizedRotation),
      };
    case 270:
      return {
        transform: `translate(${box.minX - box.minY} ${box.minY + box.width + box.minX}) rotate(270)`,
        box: getRotatedBox(box, normalizedRotation),
      };
    default:
      return {
        transform: '',
        box,
      };
  }
};

export const buildPinoutPreviewSvg = (svgContent, pins, callouts, rotation, options = {}) => {
  if (!svgContent || typeof window === 'undefined') {
    return '';
  }

  const selectedCalloutKey = typeof options.selectedCalloutKey === 'string'
    ? options.selectedCalloutKey.trim()
    : '';
  const selectedCalloutKeySet = new Set(
    (Array.isArray(options.selectedCalloutKeys) ? options.selectedCalloutKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  );
  if (selectedCalloutKey) {
    selectedCalloutKeySet.add(selectedCalloutKey);
  }
  const framePaddingRatio = Number.isFinite(Number(options.framePaddingRatio))
    ? Math.max(0.005, Number(options.framePaddingRatio))
    : 0.28;
  const minFramePadding = Number.isFinite(Number(options.minFramePadding))
    ? Math.max(4, Number(options.minFramePadding))
    : 60;
  const panX = Number.isFinite(Number(options.panX)) ? Number(options.panX) : 0;
  const panY = Number.isFinite(Number(options.panY)) ? Number(options.panY) : 0;
  const viewBoundsMode = options.viewBoundsMode === 'board' ? 'board' : 'content';
  const showEditorLineHitAreas = options.showEditorLineHitAreas === true || options.showEditorRouteHandles === true;
  const showEditorRouteHandles = options.showEditorRouteHandles === true;
  const lockedViewBox = options.lockedViewBox && Number.isFinite(Number(options.lockedViewBox.minX))
    && Number.isFinite(Number(options.lockedViewBox.minY))
    && Number.isFinite(Number(options.lockedViewBox.width))
    && Number(options.lockedViewBox.width) > 0
    && Number.isFinite(Number(options.lockedViewBox.height))
    && Number(options.lockedViewBox.height) > 0
      ? {
        minX: Number(options.lockedViewBox.minX),
        minY: Number(options.lockedViewBox.minY),
        width: Number(options.lockedViewBox.width),
        height: Number(options.lockedViewBox.height),
      }
      : null;
  const baseViewBox = options.baseViewBox && Number.isFinite(Number(options.baseViewBox.minX))
    && Number.isFinite(Number(options.baseViewBox.minY))
    && Number.isFinite(Number(options.baseViewBox.width))
    && Number(options.baseViewBox.width) > 0
    && Number.isFinite(Number(options.baseViewBox.height))
    && Number(options.baseViewBox.height) > 0
      ? {
        minX: Number(options.baseViewBox.minX),
        minY: Number(options.baseViewBox.minY),
        width: Number(options.baseViewBox.width),
        height: Number(options.baseViewBox.height),
      }
      : null;
  const viewScale = Number.isFinite(Number(options.viewScale))
    ? Math.max(0.1, Number(options.viewScale))
    : 1;

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
    return svgContent;
  }

  const box = parseSvgBox(svgContent);
  const rotationData = getRotationTransform(box, rotation);
  const calloutAutoSizeMultiplier = getCalloutAutoSizeMultiplier(rotationData.box, options);
  const preserveTags = new Set(['defs', 'style', 'title', 'desc', 'metadata']);

  svg.querySelectorAll('[data-micropinouts-callout="1"], [data-micropinouts-callout-style="1"], [data-micropinouts-rotation="1"]').forEach((node) => {
    node.remove();
  });

  if (normalizeRotation(rotation) !== 0) {
    const wrapper = createSvgElement(doc, 'g', {
      transform: rotationData.transform,
      'data-micropinouts-rotation': '1',
    });

    [...svg.childNodes].forEach((child) => {
      if (child.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      if (preserveTags.has(child.nodeName.toLowerCase())) {
        return;
      }

      wrapper.appendChild(child);
    });

    svg.appendChild(wrapper);
  }

  const rotatedPins = rotatePins(pins, svgContent, rotation);
  const normalizedCallouts = normalizePinCallouts(callouts);

  const calloutStyle = createSvgElement(doc, 'style', {
    'data-micropinouts-callout-style': '1',
  });
  calloutStyle.textContent = `
    .pin-callout-line {
      fill: none;
      stroke: #475569;
      stroke-linecap: round;
      stroke-linejoin: round;
      pointer-events: stroke;
      cursor: pointer;
    }
    .pin-callout-line-hit {
      fill: none;
      stroke: rgba(29, 78, 216, 0.001);
      stroke-linecap: round;
      stroke-linejoin: round;
      pointer-events: stroke;
      cursor: pointer;
    }
    .pin-callout-text {
      fill: #0f172a;
      font-family: Arial, sans-serif;
      dominant-baseline: middle;
      pointer-events: none;
    }
    .pin-callout-anchor-hit {
      fill: rgba(29, 78, 216, 0.001);
      stroke: none;
      cursor: grab;
      pointer-events: all;
    }
    .pin-callout-anchor {
      fill: #ffffff;
      stroke: #1d4ed8;
      cursor: grab;
      pointer-events: all;
    }
    .pin-callout-line[data-micropinouts-selected="1"] {
      stroke: #2563eb;
    }
    .pin-callout-bubble[data-micropinouts-selected="1"] {
      fill: var(--micropinouts-callout-selected-fill, #dbeafe);
    }
    .pin-callout-anchor[data-micropinouts-selected="1"] {
      fill: #dbeafe;
      stroke: #2563eb;
    }
    .pin-callout-route-point-hit {
      fill: rgba(29, 78, 216, 0.001);
      stroke: none;
      pointer-events: all;
      cursor: grab;
    }
    .pin-callout-route-point {
      fill: #ffffff;
      stroke: #2563eb;
      pointer-events: none;
    }
  `;
  svg.appendChild(calloutStyle);

  let minViewX = rotationData.box.minX;
  let minViewY = rotationData.box.minY;
  let maxViewX = minViewX + rotationData.box.width;
  let maxViewY = minViewY + rotationData.box.height;

  normalizedCallouts.forEach((callout) => {
    const calloutKey = getCalloutLayoutKey(callout);
    const isSelectedCallout = selectedCalloutKeySet.has(calloutKey);
    const calloutType = getCalloutType(callout);
    const pin = rotatedPins.find((candidate) => doesPinMatchLayoutKey(candidate, calloutKey));
    const isCustomCallout = calloutType === CUSTOM_CALLOUT_TYPE;

    if (!isCustomCallout && !pin) {
      return;
    }

    const pinName = pin ? String(pin.pin_name ?? pin.pinName ?? pin.pin_number ?? pin.pinNumber ?? 'Pin') : '';
    const groupName = pin
      ? String(pin.group_name ?? 'Other')
      : String(callout.group_name ?? callout.groupName ?? DEFAULT_CUSTOM_CALLOUT_GROUP_NAME);
    const bubbleStrokeColor = resolveCalloutBubbleStrokeColor(callout, pin);
    const selectedBubbleFill = getCalloutSelectedFillColor(bubbleStrokeColor);
    const anchorX = Number.isFinite(Number(callout.anchor_x))
      ? Number(callout.anchor_x)
      : Number(getPinX(pin));
    const anchorY = Number.isFinite(Number(callout.anchor_y))
      ? Number(callout.anchor_y)
      : Number(getPinY(pin));
    const labelX = Number(callout.label_x);
    const labelY = Number(callout.label_y);
    const label = callout.label || pinName || 'Custom Label';
    const calloutMetrics = getCalloutMetrics(label, callout);
    const calloutRotationDeg = Number.isFinite(Number(callout.rotation_deg))
      ? Number(callout.rotation_deg)
      : 0;
    const boxWidth = multiplyMetric(calloutMetrics.width, calloutAutoSizeMultiplier, 8);
    const boxHeight = multiplyMetric(calloutMetrics.height, calloutAutoSizeMultiplier, 6);
    const cornerRadius = multiplyMetric(calloutMetrics.bubbleRadius, calloutAutoSizeMultiplier, 1);
    const fontSize = multiplyMetric(calloutMetrics.fontSize, calloutAutoSizeMultiplier, 4);

    const lineWidth = multiplyMetric(
      getCalloutLineWidth(calloutMetrics.lineStrokeWidth, calloutMetrics.lineStrokeWidth),
      calloutAutoSizeMultiplier,
      MIN_CALLOUT_LINE_STROKE_WIDTH
    );

    const bubbleStrokeWidth = multiplyMetric(
      normalizeCalloutBubbleStrokeWidth(calloutMetrics.bubbleStrokeWidth, calloutMetrics.bubbleStrokeWidth),
      calloutAutoSizeMultiplier,
      MIN_CALLOUT_BUBBLE_STROKE_WIDTH
    );

    const anchorVisibleRadius = multiplyMetric(
      getCalloutAnchorVisibleRadius(calloutMetrics.anchorRadius, calloutMetrics.anchorRadius),
      calloutAutoSizeMultiplier,
      MIN_CALLOUT_ANCHOR_RADIUS
    );

    const anchorHitRadius = Math.max(
      calloutMetrics.anchorHitRadius,
      multiplyMetric(calloutMetrics.anchorHitRadius, calloutAutoSizeMultiplier, 8)
    );
    const routePoints = getCalloutRoutePoints(callout);
    const labelMidY = labelY + (boxHeight / 2);
    const labelCenterX = labelX + (boxWidth / 2);
    const labelCenterY = labelY + (boxHeight / 2);
    const customId = String(callout.custom_id ?? callout.customId ?? '').trim();
    const lineAttachSide = getResolvedCalloutLineAttachSide({
      lineAttachSide: callout.line_attach_side,
      labelCenterX,
      labelCenterY,
      anchorX,
      anchorY,
      routePoints,
    });

    const bubbleEdgePoint = getRotatedBubbleSideMidpoint({
      centerX: labelCenterX,
      centerY: labelCenterY,
      width: boxWidth,
      height: boxHeight,
      rotationDeg: calloutRotationDeg,
      side: lineAttachSide,
    });

    const entryX = bubbleEdgePoint.x;
    const entryY = bubbleEdgePoint.y;

    if (!Number.isFinite(anchorX) || !Number.isFinite(anchorY)) {
      return;
    }

    if (viewBoundsMode !== 'board') {
      const labelPadding = Math.max(24, bubbleStrokeWidth * 2);
      const anchorPadding = Math.max(8, anchorHitRadius + lineWidth);
      minViewX = Math.min(
        minViewX,
        labelX - labelPadding,
        anchorX - anchorPadding,
        entryX,
        anchorX
      );

      minViewY = Math.min(
        minViewY,
        labelY - labelPadding,
        anchorY - anchorPadding,
        entryY,
        anchorY
      );

      maxViewX = Math.max(
        maxViewX,
        labelX + boxWidth + labelPadding,
        anchorX + anchorPadding,
        entryX,
        anchorX
      );

      maxViewY = Math.max(
        maxViewY,
        labelY + boxHeight + labelPadding,
        anchorY + anchorPadding,
        entryY,
        anchorY
      );

      routePoints.forEach((routePoint) => {
        minViewX = Math.min(minViewX, routePoint.x);
        minViewY = Math.min(minViewY, routePoint.y);
        maxViewX = Math.max(maxViewX, routePoint.x);
        maxViewY = Math.max(maxViewY, routePoint.y);
      });
    }

    const group = createSvgElement(doc, 'g', {
      'data-micropinouts-callout': '1',
      'data-callout-type': calloutType,
      'data-callout-key': calloutKey,
      'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
    });

    const leaderPathD = buildLeaderPath({
      startX: entryX,
      startY: entryY,
      endX: anchorX,
      endY: anchorY,
      attachSide: lineAttachSide,
      lineStyle: callout.line_style,
      routePoints,
    });

    if (showEditorLineHitAreas) {
      const leaderHitStrokeWidth = Math.max(lineWidth + 14, 16);
      const leaderHitPath = createSvgElement(doc, 'path', {
        class: 'pin-callout-line-hit',
        d: leaderPathD,
        fill: 'none',
        stroke: 'rgba(29, 78, 216, 0.001)',
        'stroke-width': leaderHitStrokeWidth,
        style: `stroke-width: ${leaderHitStrokeWidth};`,
        'data-pin': pinName,
        'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
        'data-group': groupName,
        'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
        'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
        'data-custom-id': customId,
        'data-group-color': bubbleStrokeColor,
        'data-callout-type': calloutType,
        'data-callout-key': calloutKey,
        'data-micropinouts-callout': '1',
        'data-micropinouts-interactive': '1',
        'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
      });
      group.appendChild(leaderHitPath);
    }

    const leaderPath = createSvgElement(doc, 'path', {
      class: 'pin-callout-line',
      d: leaderPathD,
      fill: 'none',
      'stroke-width': lineWidth,
      style: `stroke-width: ${lineWidth};`,
      'data-pin': pinName,
      'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
      'data-group': groupName,
      'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
      'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
      'data-custom-id': customId,
      'data-group-color': bubbleStrokeColor,
      'data-callout-type': calloutType,
      'data-callout-key': calloutKey,
      'data-micropinouts-callout': '1',
      'data-micropinouts-interactive': '1',
      'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
    });
    group.appendChild(leaderPath);

    const anchorHitArea = createSvgElement(doc, 'circle', {
      class: 'pin-callout-anchor-hit',
      cx: anchorX,
      cy: anchorY,
      r: anchorHitRadius,
      'data-pin': pinName,
      'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
      'data-group': groupName,
      'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
      'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
      'data-custom-id': customId,
      'data-group-color': bubbleStrokeColor,
      'data-callout-type': calloutType,
      'data-callout-key': calloutKey,
      'data-micropinouts-callout': '1',
      'data-micropinouts-anchor': '1',
      'data-micropinouts-interactive': '1',
      'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
      cursor: 'grab',
      'pointer-events': 'all',
    });
    group.appendChild(anchorHitArea);

    if (showEditorRouteHandles && isSelectedCallout && routePoints.length > 0) {
      const routePointVisibleRadius = anchorVisibleRadius;
      const routePointHitRadius = anchorHitRadius;
      const routePointStrokeWidth = lineWidth;

      routePoints.forEach((routePoint, index) => {
        const routePointHitArea = createSvgElement(doc, 'circle', {
          class: 'pin-callout-route-point-hit',
          cx: routePoint.x,
          cy: routePoint.y,
          r: routePointHitRadius,
          'data-pin': pinName,
          'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
          'data-group': groupName,
          'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
          'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
          'data-custom-id': customId,
          'data-group-color': bubbleStrokeColor,
          'data-callout-type': calloutType,
          'data-callout-key': calloutKey,
          'data-route-point-index': index,
          'data-micropinouts-route-point': '1',
          'data-micropinouts-callout': '1',
          'data-micropinouts-interactive': '1',
          'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
        });
        group.appendChild(routePointHitArea);

        const routePointCircle = createSvgElement(doc, 'circle', {
          class: 'pin-callout-route-point',
          cx: routePoint.x,
          cy: routePoint.y,
          r: routePointVisibleRadius,
          fill: '#ffffff',
          stroke: '#2563eb',
          'stroke-width': routePointStrokeWidth,
          style: `stroke-width: ${routePointStrokeWidth};`,
          'data-pin': pinName,
          'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
          'data-group': groupName,
          'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
          'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
          'data-custom-id': customId,
          'data-group-color': bubbleStrokeColor,
          'data-callout-type': calloutType,
          'data-callout-key': calloutKey,
          'data-route-point-index': index,
          'data-micropinouts-route-point': '1',
          'data-micropinouts-callout': '1',
          'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
        });
        group.appendChild(routePointCircle);
      });
    }

    const anchorCircle = createSvgElement(doc, 'circle', {
      class: 'pin-callout-anchor',
      cx: anchorX,
      cy: anchorY,
      r: anchorVisibleRadius,
      fill: '#ffffff',
      stroke: '#1d4ed8',
      'stroke-width': lineWidth,
      style: `stroke-width: ${lineWidth};`,
      'data-pin': pinName,
      'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
      'data-group': groupName,
      'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
      'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
      'data-custom-id': customId,
      'data-group-color': bubbleStrokeColor,
      'data-callout-type': calloutType,
      'data-callout-key': calloutKey,
      'data-micropinouts-callout': '1',
      'data-micropinouts-anchor': '1',
      'data-micropinouts-interactive': '1',
      'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
      cursor: 'grab',
      'pointer-events': 'all',
    });
    group.appendChild(anchorCircle);

    const rect = createSvgElement(doc, 'rect', {
      class: `pin-callout-bubble group-${groupName.toLowerCase().replace(/\s+/g, '-')}`,
      x: labelX,
      y: labelY,
      width: boxWidth,
      height: boxHeight,
      rx: cornerRadius,
      ry: cornerRadius,
      fill: 'rgba(255, 255, 255, 0.94)',
      stroke: bubbleStrokeColor,
      'stroke-width': bubbleStrokeWidth,
      style: `stroke-width: ${bubbleStrokeWidth}; --micropinouts-callout-selected-fill: ${selectedBubbleFill};`,
      'data-pin': pinName,
      'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
      'data-group': groupName,
      'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
      'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
      'data-custom-id': customId,
      'data-group-color': bubbleStrokeColor,
      'data-callout-type': calloutType,
      'data-callout-key': calloutKey,
      'data-micropinouts-callout': '1',
      'data-micropinouts-interactive': '1',
      'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
      cursor: 'pointer',
      'pointer-events': 'all',
      transform: calloutRotationDeg
        ? `rotate(${calloutRotationDeg} ${labelCenterX} ${labelCenterY})`
        : undefined,
    });
    const title = createSvgElement(doc, 'title');
    title.textContent = isCustomCallout ? label : `${label} (${groupName})`;
    rect.appendChild(title);
    group.appendChild(rect);

    const text = createSvgElement(doc, 'text', {
      class: 'pin-callout-text',
      x: labelX + (boxWidth / 2),
      y: labelMidY,
      'font-size': fontSize,
      'text-anchor': 'middle',
      'data-pin': pinName,
      'data-pin-id': String(pin?.id ?? pin?.pin_id ?? pin?.pinId ?? ''),
      'data-group': groupName,
      'data-pin-number': String(pin?.pin_number ?? pin?.pinNumber ?? ''),
      'data-svgid': String(pin?.svg_id ?? pin?.svgId ?? ''),
      'data-custom-id': customId,
      'data-group-color': bubbleStrokeColor,
      'data-callout-type': calloutType,
      'data-callout-key': calloutKey,
      'data-micropinouts-callout': '1',
      'data-micropinouts-interactive': '1',
      'data-micropinouts-selected': isSelectedCallout ? '1' : undefined,
      cursor: 'pointer',
      'pointer-events': 'all',
      transform: calloutRotationDeg
        ? `rotate(${calloutRotationDeg} ${labelCenterX} ${labelCenterY})`
        : undefined,
    });
    text.textContent = label;
    group.appendChild(text);

    svg.appendChild(group);
  });

  if (lockedViewBox) {
    svg.setAttribute('viewBox', `${lockedViewBox.minX} ${lockedViewBox.minY} ${lockedViewBox.width} ${lockedViewBox.height}`);
    svg.setAttribute('width', String(lockedViewBox.width));
    svg.setAttribute('height', String(lockedViewBox.height));
  } else if (baseViewBox) {
    const baseCenterX = baseViewBox.minX + (baseViewBox.width / 2);
    const baseCenterY = baseViewBox.minY + (baseViewBox.height / 2);
    const scaledWidth = Number(Math.max(0.01, baseViewBox.width / viewScale).toFixed(2));
    const scaledHeight = Number(Math.max(0.01, baseViewBox.height / viewScale).toFixed(2));
    const scaledMinX = Number(((baseCenterX - (scaledWidth / 2)) + panX).toFixed(2));
    const scaledMinY = Number(((baseCenterY - (scaledHeight / 2)) + panY).toFixed(2));

    svg.setAttribute('viewBox', `${scaledMinX} ${scaledMinY} ${scaledWidth} ${scaledHeight}`);
    svg.setAttribute('width', String(scaledWidth));
    svg.setAttribute('height', String(scaledHeight));
  } else {
    const contentWidth = maxViewX - minViewX;
    const contentHeight = maxViewY - minViewY;
    const framePadding = Math.max(
      minFramePadding,
      Math.max(contentWidth, contentHeight) * framePaddingRatio
    );
    const expandedMinX = Number((minViewX - framePadding).toFixed(2));
    const expandedMinY = Number((minViewY - framePadding).toFixed(2));
    const expandedWidth = Number((contentWidth + (framePadding * 2)).toFixed(2));
    const expandedHeight = Number((contentHeight + (framePadding * 2)).toFixed(2));

    svg.setAttribute('viewBox', `${Number((expandedMinX + panX).toFixed(2))} ${Number((expandedMinY + panY).toFixed(2))} ${expandedWidth} ${expandedHeight}`);
    svg.setAttribute('width', String(expandedWidth));
    svg.setAttribute('height', String(expandedHeight));
  }

  return new XMLSerializer().serializeToString(doc);
};

const formatSvgNumber = (value, fallback = 0) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return fallback;
  }

  return Number(numeric.toFixed(2));
};

export const buildPolylinePath = (points = []) => {
  const normalizedPoints = (Array.isArray(points) ? points : [])
    .map((point) => {
      const x = Number(point?.x);
      const y = Number(point?.y);

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }

      return {
        x: formatSvgNumber(x),
        y: formatSvgNumber(y),
      };
    })
    .filter(Boolean);

  if (normalizedPoints.length === 0) {
    return '';
  }

  const [firstPoint, ...restPoints] = normalizedPoints;

  return [
    `M ${firstPoint.x} ${firstPoint.y}`,
    ...restPoints.map((point) => `L ${point.x} ${point.y}`),
  ].join(' ');
};

export const getResolvedCalloutLineAttachSide = ({
  lineAttachSide,
  labelCenterX,
  labelCenterY,
  anchorX,
  anchorY,
  routePoints = [],
}) => {
  let side = lineAttachSide;
  const attachTarget = normalizeCalloutRoutePoints(routePoints)[0] || {
    x: anchorX,
    y: anchorY,
  };

  if (!side || side === 'auto') {
    const dx = attachTarget.x - labelCenterX;
    const dy = attachTarget.y - labelCenterY;

    if (Math.abs(dx) >= Math.abs(dy)) {
      side = dx >= 0 ? 'right' : 'left';
    } else {
      side = dy >= 0 ? 'bottom' : 'top';
    }
  }

  return side;
};

export const buildLeaderPath = ({
  startX,
  startY,
  endX,
  endY,
  attachSide = 'auto',
  lineStyle = 'orthogonal',
  routePoints = [],
}) => {
  const sx = formatSvgNumber(startX);
  const sy = formatSvgNumber(startY);
  const ex = formatSvgNumber(endX);
  const ey = formatSvgNumber(endY);
  const normalizedRoutePoints = normalizeCalloutRoutePoints(routePoints);

  if (normalizedRoutePoints.length > 0) {
    return buildPolylinePath([
      { x: sx, y: sy },
      ...normalizedRoutePoints,
      { x: ex, y: ey },
    ]);
  }

  const dx = ex - sx;
  const dy = ey - sy;

  if (lineStyle === 'diagonal45') {
    const dirX = dx >= 0 ? 1 : -1;
    const dirY = dy >= 0 ? 1 : -1;
    const diagonalLength = Math.min(Math.abs(dx), Math.abs(dy));

    const diagonalEndX = formatSvgNumber(sx + dirX * diagonalLength);
    const diagonalEndY = formatSvgNumber(sy + dirY * diagonalLength);

    /*
      This creates:
      start -> 45-degree diagonal -> horizontal/vertical finish.
    */
    if (Math.abs(dx) >= Math.abs(dy)) {
      return `M ${sx} ${sy} L ${diagonalEndX} ${diagonalEndY} H ${ex}`;
    }

    return `M ${sx} ${sy} L ${diagonalEndX} ${diagonalEndY} V ${ey}`;
  }

  let side = attachSide;

  if (!side || side === 'auto') {
    if (Math.abs(dx) >= Math.abs(dy)) {
      side = dx >= 0 ? 'right' : 'left';
    } else {
      side = dy >= 0 ? 'bottom' : 'top';
    }
  }

  if (side === 'left' || side === 'right') {
    const routeX = formatSvgNumber(sx + dx * 0.5);
    return `M ${sx} ${sy} H ${routeX} V ${ey} H ${ex}`;
  }

  const routeY = formatSvgNumber(sy + dy * 0.5);
  return `M ${sx} ${sy} V ${routeY} H ${ex} V ${ey}`;
};
